import React, { FC, useEffect, useState } from "react";
import { Checkbox, FormControlLabel, Link } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import { Stack, Typography } from "@components/base";
import { useUserStore } from "@store/user";
import { useAcceptTermsAndConditions, useGetCurrentCompanyInfo } from "@api";
import { ConfirmationModal } from "@components/common";
import { Error } from "@helpers";

interface TermsAndConditionsModalProps {}

const TermsAndConditionsModal: FC<TermsAndConditionsModalProps> = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const { user, setUser } = useUserStore();
  const { acceptTermsAndConditionsAsync } = useAcceptTermsAndConditions(user);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const termsAndConditionsAccepted = user?.termsAndConditionsAccepted ?? false;
  const registrationAction = params.get("registrationAction");

  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);
  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setAccepted(event.target.checked);
  };

  const handleAcceptTerms = async (): Promise<void> => {
    try {
      await acceptTermsAndConditionsAsync();
      if (user) {
        setUser({
          ...user,
          termsAndConditionsAccepted: true,
        });
      }
      setIsOpen(false);
    } catch (error) {
      Error("Error accepting terms and conditions:", error);
    }
  };

  const handleDeclineTerms = (): void => {
    setIsOpen(false);
    navigate("/");
  };

  let titleContent;
  let descriptionContent;
  let confirmDisabled;

  switch (Number(registrationAction)) {
    case 7:
      titleContent = (
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6" align="center">
            <Trans
              i18nKey="csob.hub.termsAndConditions.existingAccount"
              values={{
                ico: currentCompanyInfo?.companyId,
              }}
              components={{
                ico: <span style={{ color: "blue" }} />,
              }}
            />
          </Typography>
        </Stack>
      );
      descriptionContent = (
        <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <Typography variant="body2" align="center">
            <Trans
              i18nKey="csob.hub.termsAndConditions.existingAccountDescription"
              values={{
                company: currentCompanyInfo?.name || "Your company",
              }}
            />
          </Typography>
          <Typography variant="caption" align="center">
            {t("csob.hub.termsAndConditions.existingAccountInfo")}
          </Typography>
        </Stack>
      );
      confirmDisabled = false;
      break;
    default:
      titleContent = (
        <Stack spacing={2}>
          <Typography variant="h6" align="left">
            {t("csob.hub.termsAndConditions.welcomeUser", {
              user: `${user?.firstName} ${user?.lastName}`,
            })}
          </Typography>
        </Stack>
      );
      descriptionContent = (
        <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <Typography variant="body2" textAlign="left">
            {t("csob.hub.termsAndConditions.welcomeUserText1")}
          </Typography>
          <Typography variant="body2" textAlign="left">
            {t("csob.hub.termsAndConditions.welcomeUserText2")}
          </Typography>
          <Typography variant="body2" textAlign="left">
            {t("csob.hub.termsAndConditions.welcomeUserText3")}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={accepted} onChange={handleCheckboxChange} />}
            label={
              <Typography variant="body2">
                <Trans
                  i18nKey="csob.hub.termsAndConditions.termsAndConditions"
                  components={{
                    a: (
                      <Link
                        href={
                          "https://www.greenometer.com/greenometer-platform-terms-of-use"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ),
                  }}
                />
              </Typography>
            }
          />
        </Stack>
      );
      confirmDisabled = !accepted;
      break;
  }

  useEffect(() => {
    if (user && !termsAndConditionsAccepted) {
      setIsOpen(true);
    }
  }, [termsAndConditionsAccepted, user]);

  return (
    <ConfirmationModal
      open={isOpen}
      onClose={handleDeclineTerms}
      onConfirm={handleAcceptTerms}
      title={titleContent}
      description={descriptionContent}
      confirmText={t("csob.hub.termsAndConditions.continue")}
      cancelText={t("csob.hub.termsAndConditions.cancel")}
      confirmDisabled={confirmDisabled}
      noCancel={registrationAction === "7"}
    />
  );
};

export default TermsAndConditionsModal;
